import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  approveFinding,
  needsReviewFinding,
  rejectFinding,
} from '@/api/findings';
import type {
  InvoiceExtractionOut,
  InvoiceLinkOut,
} from '@/api/invoices';
import type { FindingOut, ReasonCode, RuleOut } from '@/api/types';
import { AttachmentChip } from '@/components/AttachmentChip';
import { CarriedForwardPill } from '@/components/CarriedForwardPill';
import { FindingFilterBar } from '@/components/FindingFilterBar';
import { FindingTags } from '@/components/FindingTags';
import { NoInvoiceBadge } from '@/components/NoInvoiceBadge';
import { ReasonCodesPicker } from '@/components/ReasonCodesPicker';
import { SortableTh } from '@/components/SortableTh';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatusBadge as FindingStatusBadge } from '@/components/ui/status-badge';
import type { BulkAction, FindingsReview } from '@/hooks/useFindingsReview';
import { useReturnToRow } from '@/hooks/useReturnToRow';
import { useWindowedRows } from '@/hooks/useWindowedRows';
import {
  findingSortStorageKey,
  lastVisitedFindingKey,
  readStoredSort,
  visibleFindingOrderKey,
  writeLastVisitedRow,
  writeStoredSort,
  writeVisibleFindingOrder,
} from '@/lib/findingFilterStorage';
import {
  BULK_APPROVAL_REASON,
  bulkApprovalReason,
  defaultApprovalReason,
} from '@/lib/approval-reasons';
import {
  headerSelectionState,
  pruneToVisible,
  selectAll,
  toggleSelection,
} from '@/lib/findingSelection';
import {
  nextSortState,
  sortFindings,
  type SortKey,
  type SortState,
} from '@/lib/findingSort';
import { coerceCurrency, formatCurrency } from '@/lib/format';
import { submitFormOnCmdEnter } from '@/lib/formKeys';
import { stopRowActivation } from '@/lib/rowLink';
import { useAuth } from '../auth/AuthContext';

function stringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return value.length > 0 ? value : null;
}

// The carried-only filter lives outside the FindingFilterBar (the queue
// exposes it via the carried toggle). Because the List view shares one
// filter instance with the queue, it would silently narrow the table —
// surface it as a clearable chip so the state is visible and locally
// dismissable.
function SharedFilterChips({ review }: { review: FindingsReview }) {
  const chips: { key: string; label: string; clear: () => void }[] = [];
  if (review.carriedOnlyFilter) {
    chips.push({
      key: 'carried',
      label: 'Auto-carried only',
      clear: () => review.setCarriedOnlyFilter(false),
    });
  }
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-brand-slate">Also filtering by:</span>
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={c.clear}
          className="inline-flex items-center gap-1 rounded-full border border-brand-royal/40 bg-brand-royal/5 px-2 py-0.5 text-brand-navy transition-colors hover:border-brand-royal/70"
          title="Clear this filter"
        >
          {c.label}
          <span aria-hidden>×</span>
        </button>
      ))}
    </div>
  );
}


// One page of list rows. The practitioner pages with Prev/Next rather
// than scrolling the whole filtered set, and only the page on screen is
// rendered + has its invoice/claims data fetched.
const LIST_PAGE_SIZE = 100;

// ── List view ────────────────────────────────────────────────────────
//
// A dense, filterable table over the same findings the flashcard queue
// shows — for scanning + acting on many at once. Consumes the SHARED
// `useFindingsReview` instance (filters + ordering), so the two views
// never drift and switching tabs preserves the active filter. Per-row
// approve is one click; reject / needs-review expand an inline form for
// the reason codes + note (parity with the card footer).
export function FindingListView({
  review,
  canReview,
  runId,
  linksByTransaction,
  extractionsByDocument,
  markedTransactions,
  linksError,
  onVisibleTransactionIds,
}: {
  review: FindingsReview;
  canReview: boolean;
  runId: string;
  linksByTransaction: Map<string, InvoiceLinkOut[]>;
  extractionsByDocument: Map<string, InvoiceExtractionOut>;
  markedTransactions: Set<string>;
  linksError: boolean;
  /** Called with the current page's transaction ids so the parent can
   *  scope the invoice-link + claimed-line fetch to what's on screen. */
  onVisibleTransactionIds?: (transactionIds: string[]) => void;
}) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  // User-controlled column sort; null falls back to the shared locked
  // order. Sorting only reorders rows, so the selection set is unaffected.
  // Persisted per run so it survives navigating into a finding detail page
  // and back (which unmounts this view), matching the filter persistence.
  const sortKey = findingSortStorageKey(runId);
  const [sort, setSort] = useState<SortState | null>(() => readStoredSort(sortKey));
  const onSort = (key: SortKey) => setSort((s) => nextSortState(s, key));
  useEffect(() => {
    writeStoredSort(sortKey, sort);
  }, [sortKey, sort]);
  // Report the sort state up to the shared hook: an active column sort
  // spans the whole set, so it must use the full-set client path (forces
  // the 54k load + disables server paging). Cleared on unmount so the
  // queue tab isn't left pinned to the full-set path.
  const { setListSortActive } = review;
  useEffect(() => {
    setListSortActive(sort !== null);
    return () => setListSortActive(false);
  }, [setListSortActive, sort]);

  // Row source. Two modes:
  //  - server-paged (default, no sort): rows come from the shared queue's
  //    loaded pages (`review.queueItems`), the total is the locked server
  //    count (`review.queueTotal`), and paging forward asks the queue to
  //    load through the needed index. This is what makes the default List
  //    render without the full 54k load, and lets the 'has attachment'
  //    filter render the server `attached=true` set.
  //  - sorted (or client-full-load path): the shared full-set locked
  //    order, sorted client-side.
  const serverPagedList = review.serverPaged && sort === null;
  const allRows = useMemo(
    () =>
      serverPagedList ? review.queueItems : sortFindings(review.orderedFindings, sort),
    [serverPagedList, review.queueItems, review.orderedFindings, sort],
  );
  // The count the "Showing X–Y of N" + page math read: the server total
  // in paged mode (loaded rows may lag it), the loaded length otherwise.
  const totalRowCount = serverPagedList ? review.queueTotal : allRows.length;
  // Explicit pagination: render one page of LIST_PAGE_SIZE at a time with
  // Prev/Next, instead of an infinite windowed scroll over the whole
  // filtered set. Bounds both the rendered rows and the per-row
  // invoice/claims fetch (reported up to the parent via
  // `onVisibleTransactionIds`) to the page on screen.
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(totalRowCount / LIST_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  // In server-paged mode, ensure the queue has loaded through the last row
  // the current page needs before we slice it (prefetches ahead too).
  const { ensureQueueIndex } = review;
  useEffect(() => {
    if (!serverPagedList) return;
    ensureQueueIndex((safePage + 1) * LIST_PAGE_SIZE - 1);
  }, [serverPagedList, ensureQueueIndex, safePage]);
  useEffect(() => {
    // Clamp when the filtered set shrinks (dispositions, filter change).
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);
  useEffect(() => {
    // Jump back to the first page whenever the filtered/sorted set changes.
    setPage(0);
  }, [review.filterKey, sort]);
  const rows = useMemo(
    () => allRows.slice(safePage * LIST_PAGE_SIZE, safePage * LIST_PAGE_SIZE + LIST_PAGE_SIZE),
    [allRows, safePage],
  );
  // Page-scoped ids (what's rendered) drive selection + form pinning.
  const visibleIds = useMemo(() => rows.map((f) => f.finding_id), [rows]);
  // Full sorted order (all pages) drives finding-detail Prev/Next and the
  // return-to-row restore, so navigation spans page boundaries.
  const allVisibleIds = useMemo(() => allRows.map((f) => f.finding_id), [allRows]);
  // Report the on-screen page's transactions so the parent scopes the
  // invoice-link + claimed-line fetch to them (INP-789) — the list tab
  // otherwise fetched links for the whole filtered set.
  useEffect(() => {
    onVisibleTransactionIds?.(rows.map((f) => f.transaction_id));
  }, [rows, onVisibleTransactionIds]);
  // On return from a finding detail page, scroll the row we came from back
  // into view and highlight it so the practitioner keeps their place. Uses
  // the full order so a row on any page can be restored.
  const { lastVisitedId, containerRef } = useReturnToRow(
    lastVisitedFindingKey(runId),
    allVisibleIds,
  );
  // Jump to the page holding the returned-from row before restoring it
  // (once). Runs after the filter/sort page-reset effect above, so it wins
  // on mount; the ref keeps a later filter change from re-triggering it.
  const restoredPage = useRef(false);
  useEffect(() => {
    if (restoredPage.current || !lastVisitedId) return;
    const idx = allRows.findIndex((f) => f.finding_id === lastVisitedId);
    if (idx >= 0) {
      restoredPage.current = true;
      setPage(Math.floor(idx / LIST_PAGE_SIZE));
    }
  }, [lastVisitedId, allRows]);

  // Rows with an open inline disposition form. Windowing must never
  // unmount these (the form's typed note lives in the row component), so
  // their indices are pinned into the rendered set below.
  const [openFormIds, setOpenFormIds] = useState<ReadonlySet<string>>(() => new Set());
  const onFormOpenChange = useCallback((id: string, open: boolean) => {
    setOpenFormIds((prev) => {
      if (open === prev.has(id)) return prev;
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  // Rows that left the visible set (filter/sort change) unmount for real —
  // drop their pin so a stale id can't pin a remapped index.
  useEffect(() => {
    setOpenFormIds((prev) => pruneToVisible(prev, visibleIds));
  }, [visibleIds]);
  const pinnedIndices = useMemo(() => {
    if (openFormIds.size === 0) return undefined;
    const s = new Set<number>();
    rows.forEach((f, i) => {
      if (openFormIds.has(f.finding_id)) s.add(i);
    });
    return s;
  }, [rows, openFormIds]);

  // Windowed rendering (INP-778): the DOM holds only the visible slice
  // (plus overscan) between two spacer rows, so a 50k-row run scrolls
  // without 50k <tr>s. Row heights are measured per entry (variable —
  // expanded forms and error rows fold into their entry), and expanded
  // entries stay mounted via `pinned`. Inactive below ~200 rows.
  const { setContainer, rowWindow, pinnedBefore, pinnedAfter, windowed } = useWindowedRows(
    rows.length,
    { pinned: pinnedIndices },
  );
  const attachTableContainer = (el: HTMLDivElement | null) => {
    containerRef.current = el;
    setContainer(el);
  };
  // With windowing active the return-to row usually isn't in the DOM yet —
  // pre-scroll to its estimated offset once so the window renders it (the
  // highlight rides the isLastVisited prop whenever the row is rendered).
  const preScrolled = useRef(false);
  useEffect(() => {
    if (!windowed || preScrolled.current || !lastVisitedId) return;
    const idx = rows.findIndex((f) => f.finding_id === lastVisitedId);
    const el = containerRef.current;
    if (idx < 0 || !el) return;
    preScrolled.current = true;
    const listTop = el.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: Math.max(0, listTop + idx * 44 - window.innerHeight / 2) });
  }, [windowed, lastVisitedId, rows, containerRef]);

  // Persist the ids of the active set so the finding detail page can step
  // Prev/Next through the sequence. Sorted mode: the full filtered+sorted
  // order (all pages). Server-paged mode: the loaded page ids — Prev/Next
  // spans whatever's loaded (the guarded findIndex tolerates a missing id
  // at the edge of the loaded window).
  //
  // Known limitation (server-paged mode only): at the loaded-window edge,
  // detail-page "Next" stops even when the server has more rows — the detail
  // route has no handle on the paging cursor to fetch the adjacent page. The
  // durable fix is keyset-paged detail navigation that shares this cursor,
  // which lands with the Phase 3 pagination rework (see the findings-perf
  // plan); until then the practitioner re-enters from the list at the
  // boundary. Not worth threading the cursor through the detail route here.
  useEffect(() => {
    writeVisibleFindingOrder(visibleFindingOrderKey(runId), allVisibleIds);
  }, [runId, allVisibleIds]);

  // Bulk-on-selected: acts on exactly the checked rows (additive over the
  // shared bulk-on-filtered the queue exposes). Selection is a list-view
  // presentation concern, so it lives here rather than in the shared hook.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [bulkProgress, setBulkProgress] = useState<{
    action: BulkAction;
    done: number;
    total: number;
    errors: number;
  } | null>(null);
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [bulkApproveNote, setBulkApproveNote] = useState('');
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [bulkRejectCodes, setBulkRejectCodes] = useState<ReasonCode[]>([]);
  const [bulkFlagOpen, setBulkFlagOpen] = useState(false);
  const [bulkFlagNote, setBulkFlagNote] = useState('');

  // Drop selections whose row left the filtered/ordered set so the "N
  // selected" counter stays honest as filters narrow.
  useEffect(() => {
    setSelected((prev) => pruneToVisible(prev, visibleIds));
  }, [visibleIds]);

  const { allSelected, someSelected } = headerSelectionState(visibleIds, selected);
  const headerCheckboxRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const bulkInFlight = bulkProgress !== null && bulkProgress.done < bulkProgress.total;

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : selectAll(visibleIds));
  };

  const runBulk = async (
    action: BulkAction,
    apply: (id: string, idToken: string) => Promise<unknown>,
  ) => {
    // Guard against a second overlapping pass over the same ids (a
    // double-submit would write duplicate disposition audit events).
    if (!isSignedIn || selected.size === 0 || bulkInFlight) return;
    const idToken = await getIdToken();
    if (!idToken) return;
    const ids = Array.from(selected);
    setBulkProgress({ action, done: 0, total: ids.length, errors: 0 });
    let errors = 0;
    for (let i = 0; i < ids.length; i++) {
      try {
        await apply(ids[i], idToken);
      } catch (e) {
        errors += 1;
        console.error(`bulk ${action} failed for`, ids[i], e);
      }
      setBulkProgress({ action, done: i + 1, total: ids.length, errors });
    }
    qc.invalidateQueries({ queryKey: ['findings', 'by-run', runId, userKey] });
    qc.invalidateQueries({ queryKey: ['runs', runId, userKey] });
    setSelected(new Set());
    window.setTimeout(() => setBulkProgress(null), 1500);
  };

  const runBulkApprove = async () => {
    const note = bulkApproveNote.trim();
    if (note.length === 0) return;
    const byId = new Map(rows.map((f) => [f.finding_id, f]));
    await runBulk('approve', (id, t) =>
      approveFinding(id, t, bulkApprovalReason(byId.get(id)?.approval_reason), note),
    );
    setBulkApproveOpen(false);
    setBulkApproveNote('');
  };
  const runBulkReject = async () => {
    const reason = bulkRejectReason.trim();
    const codes = bulkRejectCodes;
    if (reason.length === 0 && codes.length === 0) return;
    await runBulk('reject', (id, t) => rejectFinding(id, reason, t, codes));
    setBulkRejectOpen(false);
    setBulkRejectReason('');
    setBulkRejectCodes([]);
  };
  const runBulkFlag = async () => {
    const note = bulkFlagNote.trim();
    if (note.length === 0) return;
    await runBulk('needs_review', (id, t) => needsReviewFinding(id, note, t));
    setBulkFlagOpen(false);
    setBulkFlagNote('');
  };

  const renderRow = (f: FindingOut, windowIndex: number) => (
    <FindingListRow
      key={f.finding_id}
      finding={f}
      windowIndex={windowIndex}
      rule={review.ruleById.get(f.rule_id) ?? null}
      canReview={canReview}
      runId={runId}
      invoiceLinks={linksByTransaction.get(f.transaction_id)}
      invoiceExtractions={extractionsByDocument}
      isMarkedNoInvoice={markedTransactions.has(f.transaction_id)}
      isLastVisited={f.finding_id === lastVisitedId}
      selected={selected.has(f.finding_id)}
      onToggleSelected={() => setSelected((prev) => toggleSelection(prev, f.finding_id))}
      selectionDisabled={bulkInFlight}
      onFormOpenChange={onFormOpenChange}
    />
  );

  return (
    <div className="grid gap-4">
      <FindingFilterBar
        ruleFilter={review.ruleFilter}
        setRuleFilter={review.setRuleFilter}
        provinceFilter={review.provinceFilter}
        setProvinceFilter={review.setProvinceFilter}
        supplierFilter={review.supplierFilter}
        setSupplierFilter={review.setSupplierFilter}
        transactionTypeFilter={review.transactionTypeFilter}
        setTransactionTypeFilter={review.setTransactionTypeFilter}
        classFilter={review.classFilter}
        setClassFilter={review.setClassFilter}
        glAccountFilter={review.glAccountFilter}
        setGlAccountFilter={review.setGlAccountFilter}
        currencyFilter={review.currencyFilter}
        setCurrencyFilter={review.setCurrencyFilter}
        outcomeFilter={review.outcomeFilter}
        setOutcomeFilter={review.setOutcomeFilter}
        statusFilter={review.statusFilter}
        setStatusFilter={review.setStatusFilter}
        approvalReasonFilter={review.approvalReasonFilter}
        setApprovalReasonFilter={review.setApprovalReasonFilter}
        searchQuery={review.searchQuery}
        setSearchQuery={review.setSearchQuery}
        amountMin={review.amountMin}
        setAmountMin={review.setAmountMin}
        amountMax={review.amountMax}
        setAmountMax={review.setAmountMax}
        effRateMin={review.effRateMin}
        setEffRateMin={review.setEffRateMin}
        effRateMax={review.effRateMax}
        setEffRateMax={review.setEffRateMax}
        reasonCodesFilter={review.reasonCodesFilter}
        setReasonCodesFilter={review.setReasonCodesFilter}
        invoiceFilter={review.invoiceFilter}
        setInvoiceFilter={review.setInvoiceFilter}
        invoiceLookupFailed={linksError}
        sourceFilter={review.sourceFilter}
        setSourceFilter={review.setSourceFilter}
        sources={review.sources}
        ledgerSourceFilter={review.ledgerSourceFilter}
        setLedgerSourceFilter={review.setLedgerSourceFilter}
        ledgerSources={review.ledgerSources}
        ruleIds={review.ruleIds}
        ruleById={review.ruleById}
        provinces={review.provinces}
        suppliers={review.suppliers}
        transactionTypes={review.transactionTypes}
        classes={review.classes}
        currencies={review.currencies}
        totalCount={review.totalCount}
        showingCount={totalRowCount}
        hasActiveFilter={review.hasActiveFilter}
        onClearAll={review.clearAllFilters}
      />

      <SharedFilterChips review={review} />

      {canReview && selected.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-brand-royal/30 bg-brand-royal/5 px-3 py-2">
          <span className="text-sm font-medium text-brand-navy">
            {selected.size} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={bulkInFlight}
              onClick={() => setBulkFlagOpen(true)}
            >
              Flag {selected.size}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={bulkInFlight}
              onClick={() => setBulkRejectOpen(true)}
            >
              Reject {selected.size}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={bulkInFlight}
              onClick={() => setBulkApproveOpen(true)}
            >
              Approve {selected.size}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={bulkInFlight}
              onClick={() => setSelected(new Set())}
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {bulkProgress !== null && (
        <div className="rounded-md border border-brand-royal/30 bg-brand-royal/5 px-3 py-2 text-sm">
          <p className="font-medium text-brand-navy">
            Bulk {bulkProgress.action}: {bulkProgress.done} of {bulkProgress.total}
            {bulkProgress.errors > 0 && (
              <span className="ml-2 text-destructive">({bulkProgress.errors} failed)</span>
            )}
          </p>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-brand-royal transition-all"
              style={{
                width: `${Math.round((bulkProgress.done / Math.max(1, bulkProgress.total)) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {totalRowCount > LIST_PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3 text-sm text-brand-slate">
          <span>
            Showing {(safePage * LIST_PAGE_SIZE + 1).toLocaleString()}–
            {Math.min((safePage + 1) * LIST_PAGE_SIZE, totalRowCount).toLocaleString()} of{' '}
            {totalRowCount.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={safePage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <span className="tabular-nums">
              Page {safePage + 1} of {pageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No findings to review</CardTitle>
            <CardDescription>
              {review.hasActiveFilter
                ? 'No findings match the active filters. Clear them to see others.'
                : 'This run produced zero matches.'}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div
          ref={attachTableContainer}
          className="overflow-x-auto rounded-md border border-border"
        >
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-brand-slate">
              <tr>
                {canReview && (
                  <th className="w-9 px-3 py-2 text-left font-medium">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      disabled={bulkInFlight}
                      onChange={toggleAll}
                      className="h-4 w-4 cursor-pointer rounded border-input accent-brand-royal disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Select all visible findings"
                    />
                  </th>
                )}
                <SortableTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                <th className="px-3 py-2 text-left font-medium">Rule</th>
                <th className="px-3 py-2 text-left font-medium">Tags</th>
                <SortableTh label="Supplier" sortKey="supplier" sort={sort} onSort={onSort} />
                <th className="px-3 py-2 text-left font-medium">GL account</th>
                <SortableTh label="Date" sortKey="date" sort={sort} onSort={onSort} />
                <SortableTh
                  label="Recovery"
                  sortKey="amount"
                  sort={sort}
                  onSort={onSort}
                  align="right"
                />
                {canReview && (
                  <th className="px-3 py-2 text-right font-medium">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {pinnedBefore.map((i) => renderRow(rows[i], i))}
              {rowWindow.topPad > 0 && (
                <tr data-window-spacer aria-hidden style={{ height: rowWindow.topPad }} />
              )}
              {rows
                .slice(rowWindow.start, rowWindow.end)
                .map((f, k) => renderRow(f, rowWindow.start + k))}
              {rowWindow.bottomPad > 0 && (
                <tr data-window-spacer aria-hidden style={{ height: rowWindow.bottomPad }} />
              )}
              {pinnedAfter.map((i) => renderRow(rows[i], i))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={bulkApproveOpen}
        onClose={() => {
          setBulkApproveOpen(false);
          setBulkApproveNote('');
        }}
      >
        <DialogHeader>
          <DialogTitle>Bulk approve {selected.size} findings?</DialogTitle>
          <DialogDescription>
            Approves every selected finding. The same note is recorded on
            each; the audit trail keeps individual reviewer + timestamp per
            finding. Already-approved stay approved; rejected flip to approved.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (bulkApproveNote.trim().length === 0) return;
            void runBulkApprove();
          }}
        >
          <textarea
            value={bulkApproveNote}
            onChange={(e) => setBulkApproveNote(e.target.value)}
            onKeyDown={submitFormOnCmdEnter}
            placeholder="Why are these approvable? (required, applies to all)"
            maxLength={256}
            required
            className="min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          <DialogFooter>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setBulkApproveOpen(false);
                setBulkApproveNote('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={bulkApproveNote.trim().length === 0 || bulkInFlight}
            >
              Approve all {selected.size}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>

      <Dialog
        open={bulkRejectOpen}
        onClose={() => {
          setBulkRejectOpen(false);
          setBulkRejectReason('');
          setBulkRejectCodes([]);
        }}
      >
        <DialogHeader>
          <DialogTitle>Bulk reject {selected.size} findings?</DialogTitle>
          <DialogDescription>
            Rejects every selected finding. The same reason + codes are
            recorded on each; the audit trail keeps individual reviewer +
            timestamp per finding.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (bulkRejectReason.trim().length === 0 && bulkRejectCodes.length === 0) return;
            void runBulkReject();
          }}
        >
          <ReasonCodesPicker value={bulkRejectCodes} onChange={setBulkRejectCodes} />
          <textarea
            value={bulkRejectReason}
            onChange={(e) => setBulkRejectReason(e.target.value)}
            onKeyDown={submitFormOnCmdEnter}
            placeholder="Optional when codes are picked — otherwise required (applies to all)"
            maxLength={256}
            className="min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          <DialogFooter>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setBulkRejectOpen(false);
                setBulkRejectReason('');
                setBulkRejectCodes([]);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              variant="destructive"
              disabled={
                (bulkRejectReason.trim().length === 0 && bulkRejectCodes.length === 0) ||
                bulkInFlight
              }
            >
              Reject all {selected.size}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>

      <Dialog
        open={bulkFlagOpen}
        onClose={() => {
          setBulkFlagOpen(false);
          setBulkFlagNote('');
        }}
      >
        <DialogHeader>
          <DialogTitle>Flag {selected.size} findings as needs review?</DialogTitle>
          <DialogDescription>
            Marks every selected finding as needs-review. The same note is
            recorded on each. Use when they can't be approved or rejected yet.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (bulkFlagNote.trim().length === 0) return;
            void runBulkFlag();
          }}
        >
          <textarea
            value={bulkFlagNote}
            onChange={(e) => setBulkFlagNote(e.target.value)}
            onKeyDown={submitFormOnCmdEnter}
            placeholder="Why can't these be decided yet? (required, applies to all)"
            maxLength={256}
            required
            className="min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          <DialogFooter>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setBulkFlagOpen(false);
                setBulkFlagNote('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={bulkFlagNote.trim().length === 0 || bulkInFlight}
            >
              Flag all {selected.size}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>
    </div>
  );
}


function FindingListRow({
  finding,
  windowIndex,
  rule,
  canReview,
  runId,
  invoiceLinks,
  invoiceExtractions,
  isMarkedNoInvoice,
  isLastVisited,
  selected,
  onToggleSelected,
  selectionDisabled,
  onFormOpenChange,
}: {
  finding: FindingOut;
  /** Index in the full (filtered + sorted) row list — the windowing
   *  hook's measurement + pinning key. */
  windowIndex: number;
  rule: RuleOut | null;
  canReview: boolean;
  runId: string;
  invoiceLinks?: InvoiceLinkOut[];
  invoiceExtractions?: Map<string, InvoiceExtractionOut>;
  isMarkedNoInvoice?: boolean;
  isLastVisited: boolean;
  selected: boolean;
  onToggleSelected: () => void;
  selectionDisabled: boolean;
  /** Reports the inline form opening/closing so the parent can pin this
   *  row into the window (an unmount would lose the typed note). */
  onFormOpenChange: (findingId: string, open: boolean) => void;
}) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  // Which inline disposition form (if any) is open beneath the row.
  // Approve is one click; reject + needs-review need a reason / note.
  const [expanded, setExpanded] = useState<null | 'reject' | 'needs_review'>(null);
  const [note, setNote] = useState('');
  const [rejectCodes, setRejectCodes] = useState<ReasonCode[]>([]);
  useEffect(() => {
    onFormOpenChange(finding.finding_id, expanded !== null);
  }, [onFormOpenChange, finding.finding_id, expanded]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['findings', 'by-run', runId, userKey] });
    qc.invalidateQueries({ queryKey: ['runs', runId, userKey] });
    qc.invalidateQueries({ queryKey: ['finding-events', finding.finding_id] });
  };
  const reset = () => {
    setNote('');
    setRejectCodes([]);
    setExpanded(null);
  };

  const approve = useMutation({
    mutationFn: async () =>
      approveFinding(
        finding.finding_id,
        (await getIdToken()) ?? '',
        defaultApprovalReason(invoiceLinks) ?? BULK_APPROVAL_REASON,
      ),
    onSuccess: () => {
      reset();
      invalidate();
    },
  });
  const reject = useMutation({
    mutationFn: async () =>
      rejectFinding(finding.finding_id, note.trim(), (await getIdToken()) ?? '', rejectCodes),
    onSuccess: () => {
      reset();
      invalidate();
    },
  });
  const needsReview = useMutation({
    mutationFn: async () =>
      needsReviewFinding(finding.finding_id, note.trim(), (await getIdToken()) ?? ''),
    onSuccess: () => {
      reset();
      invalidate();
    },
  });

  const busy = approve.isPending || reject.isPending || needsReview.isPending;
  const anyError = approve.isError || reject.isError || needsReview.isError;
  const supplier = stringOrNull(finding.evidence['supplier.name']) ?? '—';
  const glAccount = stringOrNull(finding.evidence['gl_account.name']) ?? '—';
  const billDate = stringOrNull(finding.evidence['bill_date']) ?? '—';
  const currency = coerceCurrency(finding.recovery.currency);
  const isOverridden = finding.recovery_amount_original !== null;
  // Reject needs at least one signal — a reason code OR free text.
  const canSubmitReject = note.trim().length > 0 || rejectCodes.length > 0;
  const colSpan = canReview ? 9 : 7;

  const rowTint =
    finding.status === 'approved'
      ? 'bg-emerald-50/40'
      : finding.status === 'rejected'
        ? 'bg-destructive/5'
        : finding.status === 'needs_review'
          ? 'bg-amber-50/40'
          : '';
  // The last row opened from this list gets a blue highlight (over the
  // status tint) so the practitioner can see where they navigated from.
  const highlight = isLastVisited
    ? 'bg-brand-royal/10 ring-2 ring-inset ring-brand-royal/40'
    : rowTint;

  return (
    <>
      <tr
        data-row-id={finding.finding_id}
        data-window-index={windowIndex}
        className={`relative cursor-pointer border-t border-border transition-colors hover:bg-brand-royal/5 ${highlight}`}
      >
        {canReview && (
          <td
            className="relative z-10 whitespace-nowrap px-3 py-2"
            onClick={stopRowActivation}
          >
            <input
              type="checkbox"
              checked={selected}
              disabled={selectionDisabled}
              onChange={onToggleSelected}
              className="h-4 w-4 cursor-pointer rounded border-input accent-brand-royal disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={`Select ${rule?.aa_code ?? finding.rule_id} finding`}
            />
          </td>
        )}
        <td className="whitespace-nowrap px-3 py-2">
          <FindingStatusBadge kind="finding" status={finding.status} />
          {finding.status === 'approved' && finding.approval_reason && (
            <FindingStatusBadge
              kind="approval"
              status={finding.approval_reason}
              className="ml-1.5"
            />
          )}
          <CarriedForwardPill finding={finding} />
        </td>
        <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-brand-navy">
          <Link
            to="/runs/$id/findings/$findingId"
            params={{ id: runId, findingId: finding.finding_id }}
            onClick={() =>
              writeLastVisitedRow(lastVisitedFindingKey(runId), finding.finding_id)
            }
            // Stretched link: the ::after overlay makes the whole row a real
            // anchor — left-click navigates (client-side), right / cmd /
            // middle-click open the finding in a new tab. Interactive cells
            // sit above it via `relative z-10`.
            className="underline-offset-4 after:absolute after:inset-0 after:content-[''] hover:underline"
            aria-label={`Open detail for ${rule?.aa_code ?? finding.rule_id} finding on ${supplier}`}
          >
            {rule?.aa_code ?? finding.rule_id}
          </Link>
        </td>
        <td className="whitespace-nowrap px-3 py-2">
          <FindingTags finding={finding} />
        </td>
        <td className="px-3 py-2 text-brand-navy">
          {supplier}
          {invoiceLinks && invoiceLinks.length > 0 && (
            <span className="relative z-10 ml-1.5" onClick={stopRowActivation}>
              <AttachmentChip
                links={invoiceLinks}
                extractions={invoiceExtractions}
                customerSlug={null}
                compact
              />
            </span>
          )}
          {isMarkedNoInvoice && (!invoiceLinks || invoiceLinks.length === 0) && (
            <NoInvoiceBadge />
          )}
        </td>
        <td className="px-3 py-2 text-brand-slate">{glAccount}</td>
        <td className="whitespace-nowrap px-3 py-2 text-xs text-brand-slate">
          {billDate}
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums text-brand-navy">
          {formatCurrency(finding.recovery.amount, currency)}
          {isOverridden && (
            <span
              className="ml-1 text-[10px] uppercase text-amber-600"
              title="Recovery amount overridden"
            >
              edited
            </span>
          )}
        </td>
        {canReview && (
          <td
            className="relative z-10 whitespace-nowrap px-3 py-2 text-right"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inline-flex items-center gap-1">
              <Button
                type="button"
                size="sm"
                variant="successOutline"
                className="h-7"
                disabled={busy}
                onClick={() => approve.mutate()}
                title="Approve this finding"
              >
                {approve.isPending ? '…' : 'Approve'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 border-rose-500/40 text-rose-700 hover:bg-rose-50"
                disabled={busy}
                onClick={() => setExpanded((e) => (e === 'reject' ? null : 'reject'))}
                title="Reject this finding"
              >
                Reject
              </Button>
              <Button
                type="button"
                size="sm"
                variant="cautionOutline"
                className="h-7"
                disabled={busy}
                onClick={() =>
                  setExpanded((e) => (e === 'needs_review' ? null : 'needs_review'))
                }
                title="Flag this finding for follow-up"
              >
                Flag
              </Button>
            </div>
          </td>
        )}
      </tr>
      {canReview && anyError && (
        <tr className={rowTint}>
          <td colSpan={colSpan} className="px-3 pb-2">
            <p className="text-xs text-destructive">
              Action failed — the finding wasn't updated. Check your
              connection and try again.
            </p>
          </td>
        </tr>
      )}
      {canReview && expanded !== null && (
        <tr className={rowTint}>
          <td colSpan={colSpan} className="px-3 pb-3">
            <form
              className="grid gap-2 rounded-md border border-border bg-muted/20 p-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (expanded === 'reject') {
                  if (!canSubmitReject) return;
                  void reject.mutate();
                } else {
                  if (note.trim().length === 0) return;
                  void needsReview.mutate();
                }
              }}
            >
              {expanded === 'reject' && (
                <ReasonCodesPicker value={rejectCodes} onChange={setRejectCodes} />
              )}
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  expanded === 'reject'
                    ? 'Optional when codes are picked — otherwise required'
                    : "Why can't this be decided yet? (required)"
                }
                maxLength={256}
                disabled={busy}
                className="min-h-16 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal disabled:opacity-50"
              />
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={reset}
                  disabled={busy}
                >
                  Cancel
                </Button>
                {expanded === 'reject' ? (
                  <Button
                    type="submit"
                    size="sm"
                    variant="destructive"
                    disabled={busy || !canSubmitReject}
                  >
                    {reject.isPending ? 'Rejecting…' : 'Reject'}
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    size="sm"
                    disabled={busy || note.trim().length === 0}
                  >
                    {needsReview.isPending ? 'Flagging…' : 'Flag for review'}
                  </Button>
                )}
              </div>
            </form>
          </td>
        </tr>
      )}
    </>
  );
}
